import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { Header } from "@/components/Header";
import { Hero } from "@/components/Hero";
import { Marquee } from "@/components/Marquee";
import { TrustBar } from "@/components/TrustBar";
import { OffersGrid } from "@/components/OffersGrid";
import { WhyBroker } from "@/components/WhyBroker";
import { UsatoStrip } from "@/components/UsatoStrip";
import { HowItWorks } from "@/components/HowItWorks";
import { WhatsIncluded } from "@/components/WhatsIncluded";
import { RacingBrand } from "@/components/RacingBrand";
import { Reviews } from "@/components/Reviews";
import { ConsultantCard } from "@/components/ConsultantCard";
import { PreventivoSection } from "@/components/PreventivoSection";
import { Faq } from "@/components/Faq";
import { CtaFinal } from "@/components/CtaFinal";
import { MobileStickyBar } from "@/components/MobileStickyBar";
import { Footer } from "@/components/Footer";
import { Reveal } from "@/components/Reveal";
import { useSeo, faqSchema, organizationSchema } from "@/lib/seo";
import { FAQ_HOME } from "@/data/content";

const HERO_PHOTO = "https://images.unsplash.com/photo-1607853554439-0069ec0f29b6?crop=entropy&cs=srgb&fm=jpg&q=85";

const SeoContent = () => (
  <section className="mx-auto max-w-3xl px-5 py-16 lg:px-8" data-testid="seo-content">
    <Reveal>
      <h2 className="font-display text-2xl font-bold text-xmove-ink sm:text-3xl">
        Noleggio lungo termine: l'auto nuova senza immobilizzare capitale
      </h2>
      <div className="mt-6 space-y-5 text-base leading-relaxed text-xmove-muted">
        <p>
          Il noleggio a lungo termine è la formula con cui sempre più italiani — partite IVA, aziende e
          privati — guidano un'auto nuova pagando un canone fisso mensile che include tutto: assicurazione
          RCA e Kasko, manutenzione ordinaria e straordinaria, soccorso stradale h24, protezione
          cristalli, tutela legale, infortunio conducente ed eventi atmosferici. Niente anticipo da finanziare se non vuoi, niente svalutazione, niente rivendita a
          fine vita dell'auto: restituisci le chiavi e riparti con un modello nuovo.
        </p>
        <h3 className="font-display text-xl font-bold text-xmove-ink">Perché conviene rispetto all'acquisto</h3>
        <p>
          Acquistare un'auto significa immobilizzare capitale, esporsi alla svalutazione (un'auto media perde
          oltre il 40% del valore in tre anni) e gestire in autonomia scadenze, tagliandi e imprevisti. Con il
          noleggio lungo termine ogni costo è scritto nel contratto prima di firmare: sai esattamente quanto
          spendi ogni mese, per tutta la durata. Per le partite IVA si aggiungono i vantaggi fiscali: canone
          deducibile e IVA detraibile secondo le percentuali dell'art. 164 TUIR.
        </p>
        <h3 className="font-display text-xl font-bold text-xmove-ink">Perché scegliere XMOVE</h3>
        <p>
          Chiedere un preventivo a un singolo operatore significa vedere una sola offerta. XMOVE lavora dalla
          tua parte: consultiamo per te lo stock più ampio d'Italia e ti presentiamo solo la proposta più
          conveniente a parità di servizi. Il servizio è gratuito e la consulenza è sempre compresa.
        </p>
        <h3 className="font-display text-xl font-bold text-xmove-ink">Come ottenere il tuo preventivo</h3>
        <p>
          Compila il modulo in questa pagina con nome, telefono e l'auto o il budget che hai in mente: in
          pochi minuti un consulente dedicato ti richiama con la proposta migliore per il tuo profilo.
          Se preferisci, scrivici direttamente su WhatsApp: rispondiamo subito, anche la sera.
        </p>
      </div>
    </Reveal>
  </section>
);

export default function Home() {
  useSeo({
    title: "Noleggio Lungo Termine: Offerte {Mese Anno} | XMOVE Italia",
    description:
      "Noleggio lungo termine per partite IVA, aziende e privati. Oltre 1.800 immatricolazioni: ti garantiamo sempre il miglior prezzo d'Italia. Consulenza e preventivo gratuiti.",
    path: "/",
    jsonLd: [organizationSchema, faqSchema(FAQ_HOME)],
  });

  return (
    <div className="pb-[72px] md:pb-0" data-testid="home-page">
      <Header />
      <main>
        <Hero
          eyebrow="Noleggio lungo termine multimarca"
          h1Lines={["Noleggio Lungo Termine.", "Canone chiaro, zero sorprese."]}
          subtitle="Oltre 1.800 immatricolazioni in tutta Italia. Tutto questo grazie alla garanzia di avere sempre a disposizione il miglior prezzo in Italia. Consulenza e preventivo sempre gratuiti."
          photo={HERO_PHOTO}
          photoAlt="SUV premium a noleggio lungo termine — XMOVE Italia"
        />
        <Marquee />
        <TrustBar />
        <div id="offerte" className="scroll-mt-20">
          <OffersGrid
            title="Le offerte del mese, selezionate per te"
            subtitle="Canoni reali e aggiornati, selezionati dallo stock più ampio d'Italia. Ogni offerta include RCA, Kasko, manutenzione e assistenza H24."
          />
          <div className="-mt-6 pb-10 text-center">
            <Link
              to="/offerte/"
              data-testid="home-all-offers-link"
              className="inline-flex items-center gap-2 rounded-full border-2 border-xmove-orange px-7 py-3.5 font-display text-sm font-bold text-xmove-orange transition-all hover:bg-xmove-orange hover:text-white"
            >
              Vedi tutte le offerte <ArrowRight size={16} />
            </Link>
          </div>
        </div>
        <UsatoStrip
          limit={3}
          titolo="Noleggio lungo termine usato: ultimi arrivi"
          sottotitolo="Auto appena rientrate dal noleggio, con massimo 70/80.000 km e le stesse garanzie del lungo termine: risparmio fino al 40%."
          testid="home-usato-strip"
        />
        <WhyBroker />
        <HowItWorks />
        <WhatsIncluded dark />
        <RacingBrand />
        <SeoContent />
        <Reviews />
        <ConsultantCard />
        <PreventivoSection
          idPrefix="home"
          title="Il tuo preventivo gratuito, in pochi minuti"
          subtitle="Compila il modulo: ti richiama un consulente dedicato con la proposta migliore dello stock. Nessuno spam, nessun impegno."
        />
        <Faq faqs={FAQ_HOME} />
        <CtaFinal />
      </main>
      <Footer />
      <MobileStickyBar />
    </div>
  );
}
